import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DateRangeIcon from '@mui/icons-material/DateRange';

import { colour } from './tokens';

/**
 * One control for a date range, rather than a "from" and a "to" sitting side by side.
 *
 * Two fields per range meant four fields once paid and settled both had one, which read as four
 * unrelated questions. This is one button that says what it is set to, and opens the two inputs
 * only when somebody wants to change them.
 */

/** "1 – 30 Sep", "from 1 Sep", "to 30 Sep" — whichever ends are set. */
export const rangeLabel = (from, to, name) => {
    const day = (value) => {
        const date = new Date(`${value}T00:00:00`);
        return Number.isNaN(date.getTime())
            ? value
            : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    };

    if (from && to) return `${name} ${day(from)} – ${day(to)}`;
    if (from) return `${name} from ${day(from)}`;
    if (to) return `${name} to ${day(to)}`;
    return name;
};

const DateRangeButton = ({ name, from, to, onChange, hint }) => {
    const [anchor, setAnchor] = useState(null);
    const active = Boolean(from || to);

    return (
        <>
            <Button
                size="small"
                startIcon={<DateRangeIcon sx={{ fontSize: 17 }} />}
                onClick={(event) => setAnchor(event.currentTarget)}
                sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '13px',
                    borderRadius: '10px',
                    px: 1.25,
                    color: active ? colour.primaryDeep : colour.bodySoft,
                    bgcolor: active ? colour.primaryTint : 'transparent',
                    border: `1px solid ${active ? colour.primaryTintBorder : colour.border}`,
                }}
            >
                {rangeLabel(from, to, name)}
            </Button>

            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                PaperProps={{ sx: { p: 2, borderRadius: '12px' } }}
            >
                {hint && (
                    <Typography sx={{ fontSize: '12px', color: colour.muted, mb: 1.5, maxWidth: 260 }}>
                        {hint}
                    </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                    <TextField
                        size="small"
                        type="date"
                        label={`${name} from`}
                        value={from || ''}
                        onChange={(event) => onChange({ from: event.target.value, to })}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label={`${name} to`}
                        value={to || ''}
                        onChange={(event) => onChange({ from, to: event.target.value })}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>

                <Button
                    size="small"
                    disabled={!active}
                    onClick={() => onChange({ from: '', to: '' })}
                    sx={{ mt: 1.5, textTransform: 'none', fontWeight: 700, color: colour.bodySoft }}
                >
                    Clear
                </Button>
            </Popover>
        </>
    );
};

export default DateRangeButton;
